import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import {
  fetchRevokedAccessSigners,
  RevokedAccessSigner,
} from '../utils/fetch-revoked-access-signers.util';
import { useLocale } from './use-locale.hook';

export type { RevokedAccessSigner };

export const useRevokedAccessBeforeSend = () => {
  const { messagesLocale } = useLocale();
  const [isChecking, setIsChecking] = useState(false);
  const [revokedSigners, setRevokedSigners] = useState<RevokedAccessSigner[]>([]);
  const [pendingConfirm, setPendingConfirm] = useState<(() => void) | null>(null);

  const isFlowActive = isChecking || revokedSigners.length > 0;

  const clearState = useCallback(() => {
    setRevokedSigners([]);
    setPendingConfirm(null);
  }, []);

  const checkAndProceed = useCallback(
    async (stakeholderIds: string[], onConfirm: () => void) => {
      setIsChecking(true);
      try {
        const signers = await fetchRevokedAccessSigners(stakeholderIds);
        if (signers.length === 0) {
          onConfirm();
          return;
        }

        setRevokedSigners(signers);
        setPendingConfirm(() => onConfirm);
      } catch {
        toast.error(messagesLocale['UNEXPECTED_ERROR_OCCURRED']);
      } finally {
        setIsChecking(false);
      }
    },
    [messagesLocale],
  );

  const handleConfirmRestore = useCallback(() => {
    if (!pendingConfirm) return;
    const confirm = pendingConfirm;
    clearState();
    confirm();
  }, [clearState, pendingConfirm]);

  return {
    checkAndProceed,
    clearState,
    handleConfirmRestore,
    isChecking,
    isFlowActive,
    revokedSigners,
  };
};
